import 'react-confirm-alert/src/react-confirm-alert.css';
import 'react-tooltip/dist/react-tooltip.css';

import { Edit, LocalOffer, RemoveCircleOutline } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';

import { useProduct } from '../context/ProductContext';
import { Product } from '../models/Product';
import { useConfig } from '../utils/useConfig';
import DiscountForm from '../forms/discount/DiscountForm';

interface AdminProductCardProps {
  product: Product;
  className?: string;
}

const AdminProductCard: React.FC<AdminProductCardProps> = ({
  product,
  className = '',
}) => {
  const config = useConfig();
  const { deleteProduct, deactivateDiscount } = useProduct();
  const [isDiscountFormOpen, setIsDiscountFormOpen] = useState(false);

  const handleDeleteProduct = async () => {
    const toastId = toast.loading('Deleting...', {
      position: 'top-right',
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: 0,
      theme: 'colored',
    });

    try {
      await deleteProduct(product.id);
      toast.update(toastId, {
        render: 'Product deleted!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error: Error | any) {
      console.error('Failed to delete product', error);
      toast.update(toastId, {
        render: 'Failed to delete product',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleDeleteClick = async () => {
    confirmAlert({
      title: 'Confirm deletion',
      message: 'Are you sure you want to delete this product?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => handleDeleteProduct(),
        },
        {
          label: 'No',
        },
      ],
    });
  };

  const handleDeactivateDiscount = async () => {
    if (!product.discount) return;

    confirmAlert({
      title: 'Potwierdź dezaktywację zniżki',
      message: 'Czy na pewno chcesz dezaktywować zniżkę dla tego produktu?',
      buttons: [
        {
          label: 'Tak',
          onClick: async () => {
            const toastId = toast.loading('Dezaktywowanie zniżki...', {
              position: 'top-right',
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: 0,
              theme: 'colored',
            });

            try {
              await deactivateDiscount(product.discount!.id);
              toast.update(toastId, {
                render: 'Zniżka dezaktywowana!',
                type: 'success',
                isLoading: false,
                autoClose: 3000,
              });
              // Odświeżanie strony, aby zobaczyć zmiany
              window.location.reload();
            } catch (error: any) {
              console.error('Failed to deactivate discount', error);
              toast.update(toastId, {
                render: error.message || 'Nie udało się dezaktywować zniżki',
                type: 'error',
                isLoading: false,
                autoClose: 3000,
              });
            }
          },
        },
        {
          label: 'Nie',
        },
      ],
    });
  };

  return (
    <motion.div
      className={`
        w-full relative overflow-hidden bg-white border border-gray-100 rounded-lg shadow-sm
        flex flex-row
        ${className || ''}
      `}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{
        boxShadow:
          '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)',
        translateY: -4,
      }}
      data-tooltip-id="my-tooltip"
      data-tooltip-content={window.innerWidth > 768 ? product.name : ''}
    >
      {/* Image Section */}
      <Link
        to={`/admin/produkty/edytuj/${product.id}`}
        className="w-32 h-32 m-4 relative overflow-hidden"
      >
        <motion.div
          className="w-full h-full"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        >
          <motion.img
            src={product.mobilePrimaryImage}
            alt={product.name}
            className="object-contain object-center w-full h-full transition-transform duration-300"
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </motion.div>
      </Link>

      {/* Content Section */}
      <div className="flex-grow p-4 pl-0 flex flex-col">
        {/* Product Name */}
        <Link
          to={`/admin/produkty/edytuj/${product.id}`}
          className="group mb-2"
        >
          <h3
            className={`text-gray-800 font-medium text-sm sm:text-base group-hover:text-${config.theme.mainColor}-600 transition-colors duration-200`}
          >
            {product.name.length > 40
              ? `${product.name.slice(0, 40)}...`
              : product.name}
          </h3>
          <div
            className={`w-0 group-hover:w-full h-[1px] bg-${config.theme.mainColor}-500 transition-all duration-300`}
          ></div>
        </Link>

        {/* Price and Serial Number */}
        <div className="space-y-1">
          <Typography variant="body2" color="textSecondary">
            {product.price.toFixed(2)} zł
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {product.serialNumber}
          </Typography>

          {/* Discount info if exists */}
          {product.discount && product.discount.isActive && (
            <div className="mt-1 text-xs inline-flex items-center space-x-1 bg-purple-100 text-purple-800 px-2 py-1 rounded">
              <LocalOffer fontSize="small" className="h-3 w-3" />
              <span>
                {product.discount.type === 'percentage'
                  ? `${product.discount.value}% zniżki`
                  : `${product.discount.value.toFixed(2)} zł zniżki`}
              </span>
            </div>
          )}
        </div>

        {/* Admin Controls */}
        <div className="flex justify-between items-center mt-auto">
          <div>
            <Typography variant="body2" color="textSecondary">
              {product.popularity}
            </Typography>
          </div>
          <CardActions disableSpacing className="p-0">
            <IconButton
              aria-label="edit"
              component={Link}
              to={`/admin/produkty/edytuj/${product.id}`}
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>

            {/* Przyciski zarządzania zniżkami */}
            {product.discount && product.discount.isActive ? (
              <IconButton
                aria-label="dezaktywuj zniżkę"
                color="error"
                onClick={handleDeactivateDiscount}
                size="small"
                title="Dezaktywuj zniżkę"
              >
                <RemoveCircleOutline fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                aria-label="dodaj zniżkę"
                color="primary"
                onClick={() => setIsDiscountFormOpen(true)}
                size="small"
                title="Dodaj zniżkę"
              >
                <LocalOffer fontSize="small" />
              </IconButton>
            )}

            <IconButton
              aria-label="delete"
              color="warning"
              onClick={handleDeleteClick}
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </CardActions>
        </div>
      </div>

      {/* Formularz dodawania zniżki */}
      <DiscountForm
        isOpen={isDiscountFormOpen}
        onClose={() => setIsDiscountFormOpen(false)}
        productId={product.id}
        productName={product.name}
        productPrice={product.price}
      />
    </motion.div>
  );
};

export default AdminProductCard;
